import React, { useEffect, useState } from "react";
import { api, arabicError } from "../lib/api";
import { toast } from "sonner";
import { Inbox, Eye, X } from "lucide-react";
import AdminPhotoThumb from "../components/AdminPhotoThumb";

const statusLabel = {
  pending: "قيد الانتظار",
  confirmed: "مؤكد",
  completed: "مكتمل",
  cancelled: "ملغى",
};

const AdminReservations = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/admin/reservations"); setReservations(r.data); }
      catch (e) { toast.error(arabicError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const openChat = async (r) => {
    setSelected(r);
    setMessages([]);
    try {
      const m = await api.get(`/admin/reservations/${r.id}/messages`);
      setMessages(m.data);
    } catch (e) { toast.error(arabicError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-reservations-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold text-[#333333]">جميع الحجوزات والمحادثات</h1>
        <p className="text-[#666666] mt-1">إجمالي: {reservations.length} حجز</p>
      </div>

      {loading ? (
        <div className="bg-white border border-[#7AB2B2] rounded-xl p-8 text-center text-[#666666]">جارٍ التحميل...</div>
      ) : reservations.length === 0 ? (
        <div className="bg-white border border-[#7AB2B2] rounded-xl p-12 text-center">
          <Inbox className="mx-auto text-[#7AB2B2] mb-3" size={48} />
          <p className="text-[#666666] text-lg">لا توجد حجوزات بعد</p>
        </div>
      ) : (
        <div className="bg-white border border-[#7AB2B2] rounded-xl overflow-hidden">
          <div className="md:hidden bg-[#EBF4F6] text-[#09637E] text-xs font-bold px-3 py-2 border-b border-[#7AB2B2]" data-testid="table-swipe-hint">← اسحب الجدول يميناً ويساراً لعرض بقية الأعمدة والإجراءات</div>
          <div className="overflow-x-auto">
            <table className="w-full text-right">
              <thead className="bg-[#EBF4F6]">
                <tr>
                  <th className="p-4 text-sm font-bold">المنتج</th>
                  <th className="p-4 text-sm font-bold">الطالب</th>
                  <th className="p-4 text-sm font-bold">المالك</th>
                  <th className="p-4 text-sm font-bold">الحالة</th>
                  <th className="p-4 text-sm font-bold">التاريخ</th>
                  <th className="p-4 text-sm font-bold">الإجراء</th>
                </tr>
              </thead>
              <tbody>
                {reservations.map((r) => (
                  <tr key={r.id} className="border-t border-[#EBF4F6]" data-testid={`reservation-row-${r.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {r.product && <div className="w-12 h-12 rounded overflow-hidden shrink-0"><AdminPhotoThumb photoId={r.product.id} alt="" testid={`reservation-thumb-${r.id}`} /></div>}
                        <div className="text-sm">
                          <div className="font-bold text-[#333333]">{r.product?.size || "-"}</div>
                          <div className="text-xs text-[#666666]">{r.product?.color || ""}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-[#333333]">{r.requester_name}</td>
                    <td className="p-4 text-[#333333]">{r.owner_name}</td>
                    <td className="p-4">
                      <span className="inline-block px-3 py-1 bg-[#EBF4F6] text-[#09637E] rounded-full text-xs font-bold">{statusLabel[r.status]}</span>
                    </td>
                    <td className="p-4 text-[#666666] text-sm">{new Date(r.created_at).toLocaleDateString("ar-SY")}</td>
                    <td className="p-4">
                      <button onClick={() => openChat(r)} data-testid={`view-chat-${r.id}`}
                        className="h-10 px-3 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold text-sm flex items-center gap-1 whitespace-nowrap">
                        <Eye size={14} /> المحادثة
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="chat-modal">
            <div className="border-b border-[#7AB2B2] p-5 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-extrabold text-[#333333]">محادثة الحجز</h3>
                <p className="text-sm text-[#666666]">{selected.requester_name} ↔ {selected.owner_name}</p>
              </div>
              <button onClick={() => setSelected(null)} className="w-10 h-10 rounded-lg hover:bg-[#EBF4F6] flex items-center justify-center"><X /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {messages.length === 0 ? (
                <p className="text-center text-[#666666] py-8">لا توجد رسائل بعد</p>
              ) : messages.map((m) => (
                <div key={m.id} className="bg-[#EBF4F6] rounded-lg p-3">
                  <div className="text-xs font-bold text-[#09637E]">{m.sender_name}</div>
                  <div className="text-[#333333] mt-1">{m.message}</div>
                  <div className="text-xs text-[#666666] mt-1">{new Date(m.timestamp).toLocaleString("ar-SY")}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReservations;
